import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { TransitOperationFormValues } from 'features/declaration/models/schemas/transitOperationFormSchema'
import { CountriesOfRoutingType } from 'features/declaration/models/schemas/commonConsignmentSchemas'
import useSoftDeleteFieldArray from 'shared/rhf/useSoftDeleteFieldArray'
import useCodelist from 'features/declaration/codelist/api/useCodelist'
import { blankCountryOfRoutingOfConsignment } from 'features/declaration/models/blankItems'
import FormCollapsibleColumn from 'components/p5/FormCollapsibleColumn'
import FormSelect from 'components/p5/FormSelect'
import DeleteIcon from 'components/icons/DeleteIcon'
import useDeclarationFieldRequirements
  from 'features/declaration/form/components/useDeclarationFieldRequirements'

const scope = 'countryOfRoutingOfConsignment'

function CountriesOfRouting() {
  const { t } = useTranslation()

  const [, countriesOptions] = useCodelist('COUNTRIES')

  const { control } = useFormContext<TransitOperationFormValues>()

  const {
    visibleFields,
    softRemove,
    appendWithSequence,
  } = useSoftDeleteFieldArray<TransitOperationFormValues, CountriesOfRoutingType>({
    control,
    name: scope,
  })

  const paths = useMemo(() => [
    scope,
    ...visibleFields.map((
      f,
    ) => `${scope}.${f.index}.country`),
  ], [visibleFields])

  const fieldRequirements = useDeclarationFieldRequirements<TransitOperationFormValues>('transitOperation', paths)
  const isAddForbidden = fieldRequirements[scope]?.requirement === 'forbidden'

  return (
    <section className="py-3">
      <FormCollapsibleColumn
        columnId="declaration-countries-of-routing"
        headingLabel={t('declaration.p5.countriesOfRouting')}
        fieldRule={fieldRequirements.countryOfRoutingOfConsignment}
      >
        {visibleFields.some((f) => !f.field.deleted) && (
          <div className="form-field__label">{t('declaration.p5.countryOfRouting')}</div>
        )}
        {visibleFields.map((fieldWithIndex) => {
          const countryOfRouting = fieldWithIndex.field
          const innerItemScope = `${scope}.${fieldWithIndex.index}` as const

          if (countryOfRouting.deleted) return null

          return (
            <div
              className="country-routing-row d-flex align-items-center gap-2 mb-2"
              key={`countriesOfRouting_${countryOfRouting.id}`}
            >
              <div className="input-group">
                <span className="input-group-text">
                  {t('declaration.p5.sequenceNumber')}
                  {countryOfRouting.sequenceNumber + 1}
                </span>
                <FormSelect
                  <TransitOperationFormValues>
                  field={`${innerItemScope}.country`}
                  labelKey="declaration.p5.countryOfRouting"
                  options={countriesOptions}
                  type="sync"
                  fieldRule={fieldRequirements[`${innerItemScope}.country`]}
                />
              </div>
              <button
                type="button"
                className="btn btn-sm btn-link btn-trash"
                onClick={() => softRemove(fieldWithIndex.index)}
              >
                <DeleteIcon />
              </button>
            </div>
          )
        })}
        <div className="row">
          <div className={`col-12 text-end ${isAddForbidden ? 'cursor--not-allowed' : ''}`}>
            <button
              type="button"
              className="btn btn-sm btn-primary mt-2"
              onClick={() => appendWithSequence(blankCountryOfRoutingOfConsignment)}
              disabled={isAddForbidden}
            >
              <i className="fa fa-plus me-2" />
              {t('declaration.p5.addNewRoutingCountry')}
            </button>
          </div>
        </div>
      </FormCollapsibleColumn>
    </section>
  )
}

export default CountriesOfRouting
